import { Box } from "@chakra-ui/react";
import { useFeeChargeBreakdownTableProps } from "features/fees/FeeChargeBreakdownTableProps";
import { OffsetApplicationsTableWithAllocationsDrawer } from "features/fees/offsets/FeeOffsetApplicationsTableWithAllocationsDrawer";
import { aggFuncForFeesBreakdown } from "features/fees/posted/PostedFeeChargeUtils";
import {
  useFeeChargeEventComparisonTableDataRetrieveSuspense,
  useFeeChargeEventPeriodBreakdownTableDataRetrieveSuspense,
  useFeeOffsetTableDataForFeeChargeEventRetrieveSuspense,
} from "gen/endpoints/fee-charge-event/fee-charge-event";
import React from "react";
import { useTranslation } from "react-i18next";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";

export const FeePostReviewTables: React.FC<{
  fundFamilyId: string;
  feeChargeEventId: string;
  includeDraftLateOffsets?: boolean;
}> = ({ fundFamilyId, feeChargeEventId, includeDraftLateOffsets = true }) => {
  const { t } = useTranslation(["fees"]);
  const tableProps = useFeeChargeBreakdownTableProps(fundFamilyId);

  const { data: feeChargeEventBreakdownData } =
    useFeeChargeEventPeriodBreakdownTableDataRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        is_equalized_view: false,
        include_draft_late_offsets: includeDraftLateOffsets,
      },
    );

  const { data: feeChargeEventComparisonData } =
    useFeeChargeEventComparisonTableDataRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        is_equalized_view: false,
        include_draft_late_offsets: includeDraftLateOffsets,
      },
    );

  const { data: feeOffsetApplicationsTableData } =
    useFeeOffsetTableDataForFeeChargeEventRetrieveSuspense(
      fundFamilyId,
      feeChargeEventId,
      {
        is_equalized_view: false,
      },
    );

  return (
    <>
      <Box flexShrink={0}>
        <TableBox>
          <TableBoxHeader>
            <TableBoxTitle>
              {t("fees:postFees.feeQuarterStatistic")}
            </TableBoxTitle>
            <DataTableBoxActions showFullScreen={false} />
          </TableBoxHeader>
          <TableBoxBody>
            <DataTable data={feeChargeEventComparisonData} />
          </TableBoxBody>
        </TableBox>
      </Box>
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>{t("fees:postFees.feesBreakdown")}</TableBoxTitle>
          <DataTableBoxActions />
        </TableBoxHeader>
        <TableBoxBody>
          <DataTable
            data={feeChargeEventBreakdownData}
            aggFunc={aggFuncForFeesBreakdown}
            {...tableProps}
          />
        </TableBoxBody>
      </TableBox>
      {feeOffsetApplicationsTableData.rows.length ? (
        <OffsetApplicationsTableWithAllocationsDrawer
          feeOffsetApplicationsTableData={feeOffsetApplicationsTableData}
        />
      ) : null}
    </>
  );
};
